// ============================================================
// SendBackDialog.tsx — (B4 polish, batch 2) "Send back for changes": a
// prepared / approved statement is locked; sending it back withdraws the
// preparation / approval so the details unlock. The reason is REQUIRED
// (≤ 500 characters) and recorded in the history.
// ============================================================
import React, { useId, useState } from 'react'
import ModalFrame from '../../shared/ModalFrame'
import FormField from '../../shared/FormField'
import FieldError from '../../shared/FieldError'
import { B_PRIMARY, B_SECONDARY, BIcon, INPUT, NOTE } from '../../billing/billingUi'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'

export const SEND_BACK_MAX = 500

// (Follow-ups) Opened from the statement's details AND from the Settlement
// page's "Figures locked" box (the same action, where it was clicked); a
// refused send-back is shown inside the box (`error`).
const SendBackDialog: React.FC<{ busy: boolean; onClose: () => void; onSend: (reason: string) => Promise<boolean>; error?: string | null }> = ({ busy, onClose, onSend, error }) => {
  const id = useId()
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const guard = useUnsavedChangesGuard(reason.trim() !== '')
  const err = !reason.trim() ? 'Say what needs changing.' : null
  const close = (): void => guard(onClose)
  const submit = async (): Promise<void> => {
    setTried(true)
    if (err) return
    if (await onSend(reason.trim())) onClose()
  }
  return (
    <ModalFrame size="sm" onClose={close} eyebrow="Settlement statement" eyebrowIcon={<BIcon name="undo" size={12} />} title="Send back for changes"
      subtitle="This withdraws “prepared” and “approved” so the statement can be changed."
      footerRight={<><button type="button" className={B_SECONDARY} onClick={close}>Cancel</button>
        <button type="button" className={B_PRIMARY} disabled={busy} onClick={() => { void submit() }}>{busy ? 'Sending back…' : 'Send back'}</button></>}>
      <FormField label="What needs changing" required variant="info" htmlFor={`${id}-reason`}>
        <textarea id={`${id}-reason`} className={`${INPUT} h-auto min-h-[96px] py-2`} value={reason} maxLength={SEND_BACK_MAX} required
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={(tried && !!err) || undefined} aria-describedby={`${id}-count${tried && err ? ` ${id}-err` : ''}`} />
        <span id={`${id}-err`}>{tried && <FieldError message={err ?? undefined} />}</span>
        <span id={`${id}-count`} className={`${NOTE} block mt-[4px]`}>{reason.length} / {SEND_BACK_MAX}</span>
      </FormField>
      {error && <p className="m-0 mt-[10px] text-[13px] text-casevo-danger" role="alert">{error}</p>}
    </ModalFrame>
  )
}

export default SendBackDialog
